import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

export function teacherDisplayName(info) {
  if (!info) return '';
  const full = `${info.last_name || ''} ${info.first_name || ''}`.trim();
  return full || info.username || '';
}

export function sameUsername(a, b) {
  return Boolean(a && b && String(a).toLowerCase() === String(b).toLowerCase());
}

export function useTeacherInvite() {
  const [searchParams] = useSearchParams();
  const teacherParam = (searchParams.get('teacher') || '').trim();
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [teacherWarning, setTeacherWarning] = useState('');
  const [teacherLoading, setTeacherLoading] = useState(Boolean(teacherParam));

  useEffect(() => {
    if (!teacherParam) {
      setTeacherInfo(null);
      setTeacherWarning('');
      setTeacherLoading(false);
      return;
    }
    let cancelled = false;
    setTeacherLoading(true);
    (async () => {
      try {
        const res = await api.get(`/auth/teachers/${encodeURIComponent(teacherParam)}/`);
        if (!cancelled) {
          setTeacherInfo(res.data);
          setTeacherWarning('');
        }
      } catch {
        if (!cancelled) {
          setTeacherInfo(null);
          setTeacherWarning('Преподаватель по ссылке не найден — продолжить можно без привязки.');
        }
      } finally {
        if (!cancelled) setTeacherLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [teacherParam]);

  return { teacherParam, teacherInfo, teacherWarning, teacherLoading };
}

export function bindErrorMessage(e) {
  const fallback = 'Не удалось привязаться к преподавателю.';
  const data = e?.response?.data;
  if (data == null) {
    if (e?.message === 'Network Error') {
      return 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.';
    }
    return fallback;
  }
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (!trimmed || trimmed.startsWith('<')) return fallback;
    return trimmed;
  }
  const detail = data.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) return detail.map(String).join(' ');
  if (typeof data.error === 'string' && data.error.trim()) return data.error;
  return fallback;
}

export function useBindLoggedInToTeacher(teacherInfo) {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [binding, setBinding] = useState(false);
  const [bindError, setBindError] = useState('');
  const started = useRef(false);
  const teacherUsername = teacherInfo?.username || '';

  const isOwnLink = Boolean(user && teacherInfo && sameUsername(user.username, teacherInfo.username));
  const canBind = Boolean(user && teacherUsername && !isOwnLink);

  const bind = useCallback(() => {
    if (!teacherUsername) return;
    started.current = true;
    setBinding(true);
    setBindError('');
    api.post('/auth/bind-teacher/', { teacher: teacherUsername })
      .then(() => {
        navigate('/dashboard', { replace: true });
      })
      .catch((e) => {
        started.current = false;
        setBindError(bindErrorMessage(e));
        setBinding(false);
      });
  }, [teacherUsername, navigate]);

  useEffect(() => {
    if (authLoading || !canBind || started.current) return;
    bind();
  }, [authLoading, canBind, bind]);

  return { user, authLoading, binding, bindError, isOwnLink, retryBind: bind };
}
